import React from 'react';
import { Download, Search, X } from 'lucide-react';

import { AppModal } from '../../components/AppModal';
import { InlineLoadingState } from '../../components/LoadingSpinner';
import { PdfDocumentViewer } from '../../components/PdfDocumentViewer';
import { PdfPrintButton } from '../../components/PdfPrintButton';
import { PdfMailShareButton } from '../../components/PdfMailShareButton';
import {
  documentMailTemplateValues,
  documentTextTypeFromInvoiceDocumentType,
} from '../leads/utils/documentMailDraft';
import { SageImportInvoiceReadView } from '../invoices/components/SageImportInvoiceReadView';
import { invoiceDocumentListMatches, invoiceDocumentListTitle } from '../invoices/utils/invoiceDocumentList';
import type { SageImportReadDocument } from '../invoices/components/SageImportInvoiceReadView';

export type InvoiceDocumentRow = {
  id: string;
  filename: string;
  document_type: string;
  source?: string | null;
  invoice_number?: string | null;
  issue_date: string | null;
  /** Zahlungs-/Workflow-Status (wie Rechnungsübersicht). */
  status?: string | null;
  due_date?: string | null;
  paid_at?: string | null;
  created_at: string;
  net_amount: number | null;
  gross_amount: number | null;
};

const PREVIEW_LIMIT = 10;

const formatCurrency = (v: number | null | undefined): string =>
  v == null
    ? '—'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

const formatDateDe = (s: string | null | undefined): string =>
  s ? new Date(s).toLocaleDateString('de-DE') : '—';

const DOCUMENT_TYPE_LABEL_DE: Record<string, string> = {
  offer: 'Angebot',
  invoice: 'Rechnung',
  partial_invoice: 'Abschlagsrechnung',
  final_invoice: 'Schlussrechnung',
  credit_note: 'Stornorechnung',
  invoice_correction: 'Gutschrift',
  supplier_invoice: 'Eingangsrechnung',
  incoming_invoice: 'Eingangsrechnung',
  incoming_invoice_correction: 'Lieferantengutschrift',
};

function documentTypeLabelDe(documentType: string): string {
  return DOCUMENT_TYPE_LABEL_DE[documentType] ?? documentType;
}

function dateInputValue(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function dueDeltaDays(dueDate: string | null | undefined): number | null {
  const due = dateInputValue(dueDate);
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueD = new Date(`${due}T00:00:00`);
  if (Number.isNaN(dueD.getTime())) return null;
  return Math.round((dueD.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
}

function isOfferDocument(doc: Pick<InvoiceDocumentRow, 'document_type'>): boolean {
  return doc.document_type === 'offer';
}

/** Anzeige-Label für den gespeicherten Status (Angebote vs. Rechnungen). */
function workflowStatusLabel(doc: InvoiceDocumentRow): string {
  const raw = (doc.status ?? 'open').trim().toLowerCase();
  if (doc.document_type === 'invoice_correction' && raw === 'paid') return 'Ausbezahlt';
  if (doc.document_type === 'incoming_invoice_correction' && raw === 'paid') return 'Verrechnet';
  if (doc.document_type === 'credit_note' && raw === 'paid') return 'Verschickt';
  if (doc.document_type === 'offer' && raw === 'paid') return 'Angenommen';
  if (raw === 'cancelled') return 'Storniert';
  const map: Record<string, string> = {
    active: 'Aktiv',
    open: 'Offen',
    paid: isOfferDocument(doc) ? 'Angenommen' : 'Bezahlt',
    overdue: 'Überfällig',
    draft: 'Entwurf',
    extraction_pending: 'Offen',
    extraction_failed: 'Fehler',
    rejected: 'Abgelehnt',
    cancelled: 'Storniert',
  };
  return map[raw] ?? doc.status ?? 'Offen';
}

/** Feste Badge-Farben pro Phase (bewusst hardcodiert). */
function workflowStatusBadgeClass(doc: InvoiceDocumentRow): string {
  const raw = (doc.status ?? 'open').trim().toLowerCase();
  const offer = isOfferDocument(doc);
  if (raw === 'paid') {
    return offer ? 'bg-emerald-100 text-emerald-900 border border-emerald-200/80' : 'bg-cyan-100 text-cyan-900 border border-cyan-200/80';
  }
  if (raw === 'cancelled') return 'bg-app-surface-muted text-app-text-muted border border-app-border/80';
  if (raw === 'overdue' || raw === 'extraction_failed' || raw === 'rejected') {
    return 'bg-rose-100 text-rose-800 border border-rose-200/80';
  }
  if (raw === 'draft') return 'bg-amber-100 text-amber-900 border border-amber-200/80';
  if (raw === 'active' || raw === 'open' || raw === 'extraction_pending') {
    return offer ? 'bg-indigo-100 text-indigo-900 border border-indigo-200/80' : 'bg-blue-100 text-blue-900 border border-blue-200/80';
  }
  return 'bg-app-surface-muted text-app-text-muted border border-app-border/80';
}

/**
 * Fälligkeitsdatum liegt in der Vergangenheit, Status noch `open`/`active`/`extraction_pending`
 * (nicht als `overdue` geführt) — in der UI nur „Überfällig“ zeigen, nicht zusätzlich „Offen“.
 */
function showImplicitOverdueOverlay(doc: InvoiceDocumentRow): boolean {
  const raw = (doc.status ?? 'open').trim().toLowerCase();
  if (raw === 'paid' || raw === 'cancelled' || raw === 'overdue') return false;
  const delta = dueDeltaDays(doc.due_date);
  if (delta == null || delta >= 0) return false;
  return ['open', 'active', 'extraction_pending'].includes(raw);
}

function previewTitle(doc: InvoiceDocumentRow): string {
  const num = (doc.invoice_number ?? '').trim();
  if (num) return num;
  const fn = (doc.filename ?? '').trim();
  if (fn) return fn;
  return doc.document_type || 'Dokument';
}

/** Desktop: Titel + Metadaten links, Status Mitte, Beträge rechts. */
const INVOICE_ROW_GRID =
  'grid grid-cols-[minmax(0,1fr)_auto_8rem] grid-rows-[auto_auto] gap-x-3 gap-y-0.5 items-start';

function InvoiceDocumentStatusBadges({
  doc,
  align = 'end',
}: {
  doc: InvoiceDocumentRow;
  align?: 'start' | 'end';
}) {
  const alignClass = align === 'start' ? 'justify-start' : 'justify-end';
  const implicit = showImplicitOverdueOverlay(doc);
  if (implicit) {
    return (
      <div className={`flex flex-wrap items-center ${alignClass} gap-1 max-w-[10.5rem] sm:max-w-[14rem]`}>
        <span
          className="inline-flex shrink-0 items-center rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold leading-tight text-white shadow-sm ring-2 ring-white"
          title="Fälligkeitsdatum überschritten"
        >
          Überfällig
        </span>
      </div>
    );
  }
  return (
    <div className={`flex flex-wrap items-center ${alignClass} gap-1 max-w-[10.5rem] sm:max-w-[14rem]`}>
      <span
        className={`inline-block max-w-full truncate rounded-full px-2 py-0.5 text-xs font-semibold leading-tight ${workflowStatusBadgeClass(doc)}`}
        title={workflowStatusLabel(doc)}
      >
        {workflowStatusLabel(doc)}
      </span>
    </div>
  );
}

function InvoiceDocumentRowInner({
  doc,
  onClick,
  className,
}: {
  doc: InvoiceDocumentRow;
  onClick: () => void;
  className?: string;
}) {
  const gross = doc.gross_amount;
  const net = doc.net_amount;
  const hasGross = gross != null;
  const hasNet = net != null;
  const hasPrice = hasGross || hasNet;
  const metaSpan = hasPrice ? 'col-span-2' : 'col-span-3';
  const metaLine = `${documentTypeLabelDe(doc.document_type)} · ${formatDateDe(doc.issue_date ?? doc.created_at)}`;
  return (
    <li
      role="button"
      tabIndex={0}
      className={`cursor-pointer hover:bg-app-hover active:bg-app-surface-muted/80 rounded px-2 ${className ?? ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="sm:hidden flex flex-col gap-1 py-0.5">
        <div className="flex items-start justify-between gap-3">
          <span className="text-sm font-semibold text-app-text truncate leading-snug min-w-0" title={invoiceDocumentListTitle(doc)}>
            {invoiceDocumentListTitle(doc)}
          </span>
          {hasPrice ? (
            <span className="text-sm font-semibold tabular-nums text-app-text shrink-0">
              {formatCurrency(hasGross ? gross : net)}
            </span>
          ) : null}
        </div>
        <span className="text-xs text-app-text-subtle leading-snug truncate">{metaLine}</span>
        <div className="flex items-center justify-between gap-2">
          <InvoiceDocumentStatusBadges doc={doc} align="start" />
          {hasGross && hasNet ? (
            <span className="text-xs tabular-nums text-app-text-subtle">{formatCurrency(net)}</span>
          ) : null}
        </div>
      </div>
      <div className={`hidden sm:grid ${INVOICE_ROW_GRID}`}>
        <div className="col-start-1 row-start-1 min-w-0 self-center">
          <span className="block text-sm font-semibold text-app-text truncate leading-snug" title={invoiceDocumentListTitle(doc)}>
            {invoiceDocumentListTitle(doc)}
          </span>
        </div>
        <div className="col-start-2 row-start-1 self-center justify-self-end">
          <InvoiceDocumentStatusBadges doc={doc} />
        </div>
        {hasPrice &&
          (hasGross && hasNet ? (
            <>
              <div className="col-start-3 row-start-1 text-right text-sm font-semibold tabular-nums text-app-text leading-snug self-end">
                {formatCurrency(gross)}
              </div>
              <div className={`col-start-1 row-start-2 min-w-0 ${metaSpan}`}>
                <span className="block text-xs text-app-text-subtle leading-snug truncate">{metaLine}</span>
              </div>
              <div className="col-start-3 row-start-2 text-right text-xs tabular-nums text-app-text-subtle leading-snug self-start">
                {formatCurrency(net)}
              </div>
            </>
          ) : hasGross ? (
            <>
              <div className="col-start-3 row-start-1 row-span-2 flex items-center justify-end">
                <span className="text-sm font-semibold tabular-nums text-app-text">{formatCurrency(gross)}</span>
              </div>
              <div className={`col-start-1 row-start-2 min-w-0 ${metaSpan}`}>
                <span className="block text-xs text-app-text-subtle leading-snug truncate">{metaLine}</span>
              </div>
            </>
          ) : (
            <>
              <div className="col-start-3 row-start-1 row-span-2 flex items-center justify-end">
                <span className="text-sm font-semibold tabular-nums text-app-text">{formatCurrency(net)}</span>
              </div>
              <div className={`col-start-1 row-start-2 min-w-0 ${metaSpan}`}>
                <span className="block text-xs text-app-text-subtle leading-snug truncate">{metaLine}</span>
              </div>
            </>
          ))}
        {!hasPrice && (
          <div className={`col-start-1 row-start-2 min-w-0 ${metaSpan}`}>
            <span className="block text-xs text-app-text-subtle leading-snug truncate">{metaLine}</span>
          </div>
        )}
      </div>
    </li>
  );
}

function docMatchesQuery(doc: InvoiceDocumentRow, q: string): boolean {
  const tokens = q
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  if (invoiceDocumentListMatches(doc, q)) return true;
  const hay = [
    invoiceDocumentListTitle(doc),
    doc.filename,
    doc.document_type,
    documentTypeLabelDe(doc.document_type),
    doc.invoice_number,
    previewTitle(doc),
    workflowStatusLabel(doc),
    showImplicitOverdueOverlay(doc) ? 'überfällig' : '',
    doc.due_date ? formatDateDe(doc.due_date) : '',
    formatDateDe(doc.issue_date ?? doc.created_at),
    doc.created_at ? formatDateDe(doc.created_at) : '',
    doc.net_amount != null ? formatCurrency(doc.net_amount) : '',
    doc.gross_amount != null ? formatCurrency(doc.gross_amount) : '',
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => hay.includes(t));
}

export const InvoiceDocumentsList: React.FC<{
  invoices: InvoiceDocumentRow[];
  /** Vorbelegung für „Per E-Mail senden“ (Kontakt- bzw. Rechnungs-E-Mail). */
  recipientEmail?: string | null;
  recipientCc?: string | null;
}> = ({ invoices, recipientEmail, recipientCc }) => {
  const [preview, setPreview] = React.useState<InvoiceDocumentRow | null>(null);
  const [sageRead, setSageRead] = React.useState<SageImportReadDocument | null>(null);
  const [listModalOpen, setListModalOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!listModalOpen) setSearch('');
  }, [listModalOpen]);

  React.useEffect(() => {
    if (!preview || preview.source !== 'sage_import') {
      setSageRead(null);
      return;
    }
    let cancelled = false;
    setSageRead(null);
    fetch(`/api/invoices/${encodeURIComponent(preview.id)}`, { credentials: 'include' })
      .then(async (response) => (response.ok ? response.json() : null))
      .then((row: { sage_read?: SageImportReadDocument } | null) => {
        if (!cancelled) setSageRead(row?.sage_read ?? null);
      })
      .catch(() => {
        if (!cancelled) setSageRead(null);
      });
    return () => {
      cancelled = true;
    };
  }, [preview]);

  React.useEffect(() => {
    if (!preview) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreview(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview]);

  const previewRows = invoices.slice(0, PREVIEW_LIMIT);
  const showOpenAll = invoices.length > PREVIEW_LIMIT;
  const filteredModal = React.useMemo(
    () => invoices.filter((d) => docMatchesQuery(d, search)),
    [invoices, search],
  );

  const fileUrl = preview ? `/api/invoices/${encodeURIComponent(preview.id)}/file` : '';

  const openDocFromList = (doc: InvoiceDocumentRow) => {
    setListModalOpen(false);
    setPreview(doc);
  };

  return (
    <>
      <ul className="divide-y divide-app-border/60">
        {previewRows.map((doc) => (
          <InvoiceDocumentRowInner key={doc.id} doc={doc} className="py-2" onClick={() => setPreview(doc)} />
        ))}
      </ul>
      {showOpenAll && (
        <div className="mt-3 pt-1 border-t border-app-border/40">
          <button
            type="button"
            onClick={() => setListModalOpen(true)}
            className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline"
          >
            Alle Dokumente anzeigen
          </button>
        </div>
      )}

      {showOpenAll && (
        <AppModal
          open={listModalOpen}
          onClose={() => setListModalOpen(false)}
          overlayClassName="z-[100]"
          panelClassName="md:max-w-lg w-full"
          ariaLabelledBy="invoice-documents-list-modal-title"
        >
          <div className="flex flex-col min-h-0 max-h-[85vh] md:max-h-[90vh]">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-app-border/60 shrink-0">
              <div>
                <h2 id="invoice-documents-list-modal-title" className="text-sm font-semibold text-app-text">
                  Angebote &amp; Rechnungen
                </h2>
                <p className="text-xs text-app-text-subtle mt-0.5">{invoices.length} Einträge</p>
              </div>
              <button
                type="button"
                onClick={() => setListModalOpen(false)}
                className="p-2 rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted"
                aria-label="Schließen"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-app-border/60 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Dateiname, Typ, Status, Nummer, Datum …"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-app-border rounded-lg bg-app-surface focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                  autoFocus
                />
              </div>
            </div>
            <div className="app-scrollbar flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pb-4">
              {filteredModal.length === 0 ? (
                <p className="text-sm text-app-text-subtle px-2 py-6 text-center">Keine Treffer.</p>
              ) : (
                <ul className="divide-y divide-app-border/60">
                  {filteredModal.map((doc) => (
                    <InvoiceDocumentRowInner
                      key={doc.id}
                      doc={doc}
                      className="py-2.5 rounded-md"
                      onClick={() => openDocFromList(doc)}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        </AppModal>
      )}

      <AppModal
        open={preview != null}
        onClose={() => setPreview(null)}
        overlayClassName="z-[100]"
        panelClassName="w-full md:max-w-5xl flex flex-col h-[90dvh] min-h-[82dvh] md:!h-[92vh] md:!min-h-[88vh] md:!max-h-[92vh]"
        ariaLabelledBy="invoice-doc-preview-title"
      >
        {preview && (
          <div className="flex flex-col flex-1 min-h-0 h-full overflow-hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-app-border bg-app-surface-muted shrink-0">
              <div className="min-w-0 flex-1">
                <h2 id="invoice-doc-preview-title" className="text-sm font-semibold text-app-text truncate">
                  {previewTitle(preview)}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <p className="text-xs text-app-text-subtle">
                    {documentTypeLabelDe(preview.document_type)} · {formatDateDe(preview.issue_date ?? preview.created_at)}
                  </p>
                  <InvoiceDocumentStatusBadges doc={preview} />
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {preview.source !== 'sage_import' && /\.pdf$/i.test(preview.filename || '') ? (
                  <>
                    <PdfMailShareButton
                      url={fileUrl}
                      filename={preview.filename}
                      documentTextType={documentTextTypeFromInvoiceDocumentType(preview.document_type)}
                      templateValues={documentMailTemplateValues(preview)}
                      to={recipientEmail ?? undefined}
                      cc={recipientCc ?? undefined}
                      onBeforeOpen={() => setPreview(null)}
                      className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                    />
                    <PdfPrintButton
                      url={fileUrl}
                      className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                    />
                  </>
                ) : null}
                <a
                  href={`${fileUrl}?download=1`}
                  download={preview.filename || true}
                  className="inline-flex items-center gap-2 rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-medium text-app-text-muted hover:bg-app-hover"
                >
                  <Download className="w-4 h-4" aria-hidden />
                  Herunterladen
                </a>
                <button
                  type="button"
                  onClick={() => setPreview(null)}
                  className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-app-text-muted"
                  aria-label="Schließen"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {preview.source === 'sage_import' ? (
              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {sageRead ? <SageImportInvoiceReadView document={sageRead} /> : <InlineLoadingState label="Beleg wird geladen …" />}
              </div>
            ) : (
            <PdfDocumentViewer
              url={fileUrl}
              title={preview.filename || 'Dokument'}
              className="min-h-0 w-full"
            />
            )}
          </div>
        )}
      </AppModal>
    </>
  );
};
